/**
 * dd-card2.js — interactive expanded product card
 *
 * The Liquid snippet `snippets/dd-product-card-expanded.liquid` emits a
 * <dd-card2> custom element with:
 *   - an embedded variants JSON payload
 *   - clickable length pills + color swatches with data-option-position
 *     + data-option-value attributes
 *   - a CTA button (data-cta) whose label + disabled state this component
 *     manages based on current selection
 *
 * The component:
 *   - Tracks selected option values by position (1, 2, 3)
 *   - Matches against the variants list to update hero image + price
 *     + selected-color label whenever a value is picked
 *   - Reflects Regios DOPP account tier / sitewide automatic discounts
 *     in the "Save X%" pill and price display by calling
 *     `window.RegiosDOPP.api.v0.productPage.calculateDiscountedPrices`
 *     per variant — mirrors the PDP logic in `dd-discount-pill.js`
 *   - Toggles the CTA between "Select Options" (disabled) and "Add to
 *     cart" (enabled) based on whether every "required" option position
 *     has been selected
 *   - On CTA click, adds the selected variant via window.DDCartApi and
 *     dispatches `cart:updated` + `cart:refresh` so the cart drawer
 *     refreshes without a page reload.
 */
(() => {
  if (window.customElements.get('dd-card2')) return;

  const ADDED_LABEL = 'Added';
  const ADDING_LABEL = 'Adding…';
  // Fallback only — the canonical label is set server-side via
  // `data-label-unavailable` in snippets/dd-product-card-expanded.liquid
  // ("Awaiting restock"). This default is used only when the snippet
  // is rendered without the data attr (legacy callers / tests).
  const UNAVAILABLE_LABEL_FALLBACK = 'Awaiting restock';

  /* ── Shared Regios state ────────────────────────────────────────────
     A single subscription listens for the Regios DOPP v0 API
     (via both the official `regios-dopp:api-initialized` event and a
     polling fallback) and notifies every card once it's ready.

     API choice: related-products cards render products that are NOT
     the current PDP product, so we use the generic
       api.v0.calculateDiscountedPrices({ productId, variantId, regularPriceInCents })
     form (see dd-cart-cross-sell.js + dd-mini-cart.js for prior art).
     `api.v0.productPage.calculateDiscountedPrices` is scoped to the
     current PDP and returns nothing useful for other products, which
     is why v1 of this integration appeared "silent".

     Cache is keyed by variantId since DOPP discounts are variant-
     level. A rail of 20 cards with duplicate variants only triggers
     one request per variant. */
  let regiosV0 = null;
  let regiosListening = false;
  const regiosSubscribers = new Set();
  const regiosCache = Object.create(null); // { [variantId]: {pct, saleCents, compareCents, name} | null }
  const regiosInFlight = Object.create(null); // { [variantId]: Promise<entry|null> }

  function onRegiosReady(v0) {
    regiosV0 = v0;
    regiosSubscribers.forEach((card) => card._onRegiosReady());
  }

  function startRegiosWatcher() {
    if (regiosListening) return;
    regiosListening = true;

    // Official DOPP API init event — fires once, regardless of load order.
    window.addEventListener('regios-dopp:api-initialized', function (e) {
      const v0 =
        (e && e.detail && e.detail.api) ||
        (window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0);
      if (v0 && !regiosV0) onRegiosReady(v0);
    });

    // Poll fallback for scripts that attach the global without dispatching the event.
    if (window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0) {
      onRegiosReady(window.RegiosDOPP.api.v0);
      return;
    }
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      const v0 =
        window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0;
      if (v0) {
        clearInterval(timer);
        if (!regiosV0) onRegiosReady(v0);
      } else if (attempts >= 60) {
        clearInterval(timer);
      }
    }, 500);
  }

  function fetchRegios(productId, variantId, priceCents) {
    const vid = String(variantId);
    if (regiosCache[vid] !== undefined) return Promise.resolve(regiosCache[vid]);
    if (regiosInFlight[vid]) return regiosInFlight[vid];
    if (!regiosV0 || typeof regiosV0.calculateDiscountedPrices !== 'function') {
      return Promise.resolve(null);
    }

    const p = regiosV0
      .calculateDiscountedPrices({
        productId: parseInt(productId, 10),
        variantId: parseInt(vid, 10),
        regularPriceInCents: parseInt(priceCents, 10),
      })
      .then((result) => {
        if (!result || !result.salePrice || !result.regularPrice) {
          regiosCache[vid] = null;
          return null;
        }
        const saleAmt = parseFloat(result.salePrice.amount);
        const regAmt = parseFloat(result.regularPrice.amount);
        if (!(saleAmt < regAmt)) {
          regiosCache[vid] = null;
          return null;
        }
        const saleCents = Math.round(saleAmt * 100);
        const compareCents = Math.round(regAmt * 100);
        const pct = Math.floor(((compareCents - saleCents) * 100) / compareCents);

        // Extract a plain-text discount name from Regios' HTML payload.
        let name = '';
        const html =
          (result.description && result.description.html) ||
          result.discountMessage ||
          '';
        if (html) {
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          name = (tmp.textContent || tmp.innerText || '').trim();
        }

        const entry = { pct, saleCents, compareCents, name };
        regiosCache[vid] = entry;
        return entry;
      })
      .catch(() => {
        // Silent — Liquid baseline stays visible as the fallback.
        return null;
      })
      .finally(() => {
        delete regiosInFlight[vid];
      });

    regiosInFlight[vid] = p;
    return p;
  }

  function formatMoney(cents) {
    const fmt = window.shopMoneyFormat
      ? String(window.shopMoneyFormat).replace(/<[^>]*>/g, '')
      : '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const out = fmt
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    // Match Liquid's `money_without_trailing_zeros`: strip ".00" / ",00"
    // but keep non-zero decimals and trailing currency codes intact.
    return out.replace(/([.,])00(?!\d)/, '');
  }

  class DdCard2 extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.variants = this._readVariants();
      this.requiredPositions = this._readRequiredPositions();
      this.lengthPosition = Number(this.dataset.lengthPosition) || null;
      this.colorPosition = Number(this.dataset.colorPosition) || null;
      this.labels = {
        select: this.dataset.labelSelect || 'Select Options',
        add: this.dataset.labelAdd || 'Add to cart',
        unavailable: this.dataset.labelUnavailable || UNAVAILABLE_LABEL_FALLBACK,
      };

      this.selection = { 1: null, 2: null, 3: null };
      this._applyInitialSelection();

      this.heroEl = this.querySelector('[data-hero-image]');
      // Sibling <img> emitted only when the product has > 1 image. Capture
      // its initial src so we can revert to the product-level lifestyle
      // shot for variants that don't carry their own second image.
      this.hoverImageEl = this.querySelector('[data-hover-image]');
      this.defaultHoverSrc = this.hoverImageEl ? this.hoverImageEl.getAttribute('src') || '' : '';
      this.priceEl = this.querySelector('[data-price]');
      this.colorLabelEl = this.querySelector('[data-selected-color]');
      this.colorBadgeEl = this.querySelector('[data-color-badge]');
      this.saleBadgeEl = this.querySelector('[data-sale-badge]');
      this.saleBadgeLabelEl = this.querySelector('[data-sale-badge-label]');
      this.ctaEl = this.querySelector('[data-cta]');
      this.ctaLabelEl = this.querySelector('[data-cta-label]');

      // Quantity stepper — value lives on the card instance, the DOM
      // element only mirrors it for display. Starts at 1 and is clamped
      // to >= 1; upper bound is enforced by Shopify inventory at add time.
      this.qtyValueEl = this.querySelector('[data-qty-value]');
      this.qtyDecEl = this.querySelector('[data-qty-decrement]');
      this.qtyIncEl = this.querySelector('[data-qty-increment]');
      this.quantity = 1;
      this._updateQtyUi();

      this.productId = this.dataset.productId || '';

      this.addEventListener('click', (event) => this._onClick(event));

      // Subscribe to the shared Regios watcher — `_onRegiosReady` will
      // fire once the DOPP v0 API lands so this card can re-price.
      regiosSubscribers.add(this);
      startRegiosWatcher();
      if (regiosV0) this._onRegiosReady();

      this._refresh();
    }

    disconnectedCallback() {
      regiosSubscribers.delete(this);
    }

    /** Called by the shared watcher once the Regios DOPP v0 API is ready. */
    _onRegiosReady() {
      const v = this._matchVariant();
      if (v && v.id) this._ensureRegios(v);
    }

    /**
     * Kick off a Regios fetch for the given variant if not cached /
     * in-flight. When a tier or automatic discount lands, re-run
     * `_refresh()` so the price + pill update.
     */
    _ensureRegios(variant) {
      if (!variant || !variant.id || !this.productId) return;
      if (!regiosV0) return;
      const vid = String(variant.id);
      if (regiosCache[vid] !== undefined) return;
      const priceCents = typeof variant.priceCents === 'number' ? variant.priceCents : 0;
      if (!priceCents) return;
      fetchRegios(this.productId, variant.id, priceCents).then((entry) => {
        // Only refresh if this card still cares about that variant.
        const current = this._matchVariant();
        if (current && String(current.id) === vid && entry != null) {
          this._refresh();
        }
      });
    }

    _readVariants() {
      const script = this.querySelector('script[data-variants-json]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (err) {
        console.warn('[dd-card2] invalid variants JSON', err);
        return [];
      }
    }

    _readRequiredPositions() {
      const raw = (this.dataset.requiredPositions || '').trim();
      if (!raw) return [];
      return raw
        .split(',')
        .map((n) => Number(n))
        .filter((n) => Number.isInteger(n) && n >= 1 && n <= 3);
    }

    /**
     * Apply any server-seeded selections (e.g. an auto-hidden "Texture:
     * Straight" for SecreTapes / Keratin Tips / SuperWefts). These
     * selections are not rendered as pills/swatches but still count
     * toward variant matching, so the shopper never has to pick them.
     */
    _applyInitialSelection() {
      const raw = this.dataset.initialSelection;
      if (!raw) return;
      try {
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return;
        arr.forEach((entry) => {
          if (!entry) return;
          const pos = Number(entry.position);
          const value = typeof entry.value === 'string' ? entry.value : '';
          if (pos >= 1 && pos <= 3 && value) {
            this.selection[pos] = value;
          }
        });
      } catch (err) {
        console.warn('[dd-card2] invalid initial selection', err);
      }
    }

    _onClick(event) {
      // Qty stepper — handled before the generic option-button check so the
      // −/+ buttons (which don't carry option data attrs) intercept cleanly.
      const qtyBtn = event.target.closest('[data-qty-decrement], [data-qty-increment]');
      if (qtyBtn && this.contains(qtyBtn)) {
        event.preventDefault();
        if (qtyBtn.hasAttribute('disabled')) return;
        const dir = qtyBtn.hasAttribute('data-qty-increment') ? 1 : -1;
        this._setQuantity(this.quantity + dir);
        return;
      }

      const optionBtn = event.target.closest('[data-option-position][data-option-value]');
      if (optionBtn) {
        if (optionBtn.getAttribute('data-available') === 'false') return;
        event.preventDefault();
        const pos = Number(optionBtn.getAttribute('data-option-position'));
        const val = optionBtn.getAttribute('data-option-value');
        this._select(pos, val);
        return;
      }

      const cta = event.target.closest('[data-cta]');
      if (cta && cta === this.ctaEl) {
        if (this.ctaEl.hasAttribute('disabled')) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        this._addToCart();
      }
    }

    _setQuantity(next) {
      const clamped = Math.max(1, Math.floor(Number(next) || 1));
      if (clamped === this.quantity) return;
      this.quantity = clamped;
      this._updateQtyUi();
    }

    _updateQtyUi() {
      if (this.qtyValueEl) this.qtyValueEl.textContent = String(this.quantity);
      // Decrement is disabled at the floor so shoppers can't tap below 1.
      if (this.qtyDecEl) {
        if (this.quantity <= 1) {
          this.qtyDecEl.setAttribute('disabled', '');
          this.qtyDecEl.setAttribute('aria-disabled', 'true');
        } else {
          this.qtyDecEl.removeAttribute('disabled');
          this.qtyDecEl.setAttribute('aria-disabled', 'false');
        }
      }
    }

    _select(position, value) {
      // Toggle-off if the same value is tapped twice.
      if (this.selection[position] === value) {
        this.selection[position] = null;
      } else {
        this.selection[position] = value;
      }
      this._refresh();
    }

    /**
     * Returns the variant that matches the current partial selection.
     * Falls back to the first available variant when nothing is selected.
     */
    _matchVariant() {
      const sel = this.selection;
      const matchesPartial = (v) => {
        for (let pos = 1; pos <= 3; pos++) {
          if (sel[pos] != null && v.options[pos - 1] !== sel[pos]) {
            return false;
          }
        }
        return true;
      };
      // Prefer an exact match that is also available.
      const partial = this.variants.filter(matchesPartial);
      if (partial.length === 0) return null;
      const available = partial.find((v) => v.available);
      return available || partial[0];
    }

    _isSelectionComplete() {
      return this.requiredPositions.every((pos) => this.selection[pos] != null);
    }

    _refresh() {
      this._updateOptionButtons();
      const variant = this._matchVariant();
      const pricing = this._computePricing(variant);
      this._updateHero(variant);
      this._updatePrice(variant, pricing);
      this._updateSaleBadge(pricing);
      this._updateColorLabel();
      this._updateCta(variant);

      // Lazy-load Regios data for this variant if we haven't yet.
      if (variant) this._ensureRegios(variant);
    }

    /**
     * Produce the effective pricing for a variant by layering sources
     * in the same priority the PDP's `dd-discount-pill.js` uses:
     *   1. Baseline: variant.compareAt vs. variant.price (MSRP markdown)
     *   2. Regios DOPP override: account tier + sitewide automatic
     *      discounts that calculate a lower sale price than baseline
     * Returns `null` when no variant is matched.
     */
    _computePricing(variant) {
      if (!variant) return null;

      const priceCents = typeof variant.priceCents === 'number' ? variant.priceCents : null;
      const compareCents =
        typeof variant.compareAtCents === 'number' && variant.compareAtCents > priceCents
          ? variant.compareAtCents
          : null;

      let saleCents = priceCents;
      let effectiveCompareCents = compareCents;
      let pct = compareCents
        ? Math.floor(((compareCents - priceCents) * 100) / compareCents)
        : 0;
      let discountName = '';
      let saleText = variant.price || '';
      let compareText = variant.compareAt || '';
      let source = compareCents ? 'compareAt' : 'none';

      // Layer Regios on top if it produces a deeper discount than MSRP.
      const vid = variant.id != null ? String(variant.id) : null;
      if (vid && regiosCache[vid]) {
        const r = regiosCache[vid];
        if (r && r.pct > pct) {
          pct = r.pct;
          saleCents = r.saleCents;
          effectiveCompareCents = r.compareCents;
          discountName = r.name || '';
          saleText = formatMoney(r.saleCents);
          compareText = formatMoney(r.compareCents);
          source = 'regios';
        }
      }

      return {
        onSale: pct > 0 && effectiveCompareCents != null,
        pct,
        saleCents,
        compareCents: effectiveCompareCents,
        saleText,
        compareText,
        discountName,
        source,
      };
    }

    /**
     * Sync the "Save X%" pill with the effective pricing. Percentage +
     * visual language mirror `.dd-discount-pill-banner` on the PDP.
     */
    _updateSaleBadge(pricing) {
      if (!this.saleBadgeEl) return;
      if (!pricing || !pricing.onSale) {
        this.saleBadgeEl.setAttribute('hidden', '');
        return;
      }
      if (this.saleBadgeLabelEl) {
        this.saleBadgeLabelEl.textContent = `Save ${pricing.pct}%`;
      }
      this.saleBadgeEl.removeAttribute('hidden');
    }

    _updateOptionButtons() {
      const sel = this.selection;
      this.querySelectorAll('[data-option-position][data-option-value]').forEach((btn) => {
        const pos = Number(btn.getAttribute('data-option-position'));
        const val = btn.getAttribute('data-option-value');
        const isSelected = sel[pos] === val;
        btn.classList.toggle('dd-card2__length-pill--active', isSelected && btn.classList.contains('dd-card2__length-pill'));
        btn.classList.toggle('dd-card2__swatch--selected', isSelected && btn.classList.contains('dd-card2__swatch'));
        btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });
    }

    _updateHero(variant) {
      // Primary image — swap to the variant's first image when we have
      // one. <img> vs placeholder <div> safety check preserved.
      if (variant && variant.image && this.heroEl && this.heroEl.tagName === 'IMG') {
        if (this.heroEl.getAttribute('src') !== variant.image) {
          this.heroEl.setAttribute('src', variant.image);
        }
      }
      this._updateHoverImage(variant);
    }

    /**
     * Hover image swap. Fallback order:
     *   1. `variant.hoverImage` — second metafield image (distinct hover)
     *      or, for single-image variants, the same URL as the primary so
     *      the cross-fade is a visual no-op. Liquid collapses these two
     *      cases so JS always receives a variant-specific src when one
     *      exists.
     *   2. `this.defaultHoverSrc` — product-level lifestyle shot captured
     *      at boot, used when no variant is selected or the matched
     *      variant has no image data at all.
     */
    _updateHoverImage(variant) {
      if (!this.hoverImageEl) return;
      const target = (variant && variant.hoverImage) || this.defaultHoverSrc || '';
      if (!target) return;
      if (this.hoverImageEl.getAttribute('src') !== target) {
        this.hoverImageEl.setAttribute('src', target);
      }
    }

    _updatePrice(variant, pricing) {
      if (!variant || !this.priceEl) return;
      const computed = pricing || this._computePricing(variant);
      if (!computed) return;

      this.priceEl.classList.toggle('dd-card2__price--on-sale', computed.onSale);

      // Rebuild the price DOM in place so regular ↔ sale layout switches cleanly.
      if (computed.onSale) {
        this.priceEl.innerHTML = `
          <span class="dd-card2__price-compare" data-price-compare>${computed.compareText}</span>
          <span class="dd-card2__price-sale" data-price-value>${computed.saleText}</span>
        `;
      } else {
        this.priceEl.innerHTML = `
          <span class="dd-card2__price-regular" data-price-value>${computed.saleText || variant.price}</span>
        `;
      }
    }

    _updateColorLabel() {
      if (!this.colorPosition) return;
      const name = this.selection[this.colorPosition];
      if (this.colorLabelEl) {
        this.colorLabelEl.textContent = name || '—';
      }
      // Image overlay badge — only visible once a color is picked so the
      // unselected state stays clean.
      if (this.colorBadgeEl) {
        if (name) {
          this.colorBadgeEl.textContent = name;
          this.colorBadgeEl.removeAttribute('hidden');
        } else {
          this.colorBadgeEl.setAttribute('hidden', '');
          this.colorBadgeEl.textContent = '';
        }
      }
    }

    _updateCta(variant) {
      if (!this.ctaEl || !this.ctaLabelEl) return;
      const complete = this._isSelectionComplete();

      // "Select Options" — user hasn't picked all required options yet.
      // This is a *neutral* disabled state ("you have something to do"),
      // styled in CSS as the gray pill. Keep `is-unavailable` off so the
      // destructive red treatment doesn't bleed into a recoverable state.
      if (!complete) {
        this.ctaEl.setAttribute('disabled', '');
        this.ctaEl.setAttribute('aria-disabled', 'true');
        this.ctaEl.classList.add('is-disabled');
        this.ctaEl.classList.remove('is-unavailable');
        this.ctaLabelEl.textContent = this.labels.select;
        return;
      }

      // "Unavailable" — selection is complete but the matched variant is
      // out of stock (or no matching variant exists). This is a *terminal*
      // disabled state (nothing the user can do here), so we layer
      // `is-unavailable` on top of `is-disabled` to swap the pill from
      // neutral gray to destructive red — matches the existing
      // `--dd-error-*` semantic tokens used in mini-cart errors and the
      // invalid-discount pill, so the visual language is consistent
      // across the cart/checkout journey.
      if (!variant || !variant.available) {
        this.ctaEl.setAttribute('disabled', '');
        this.ctaEl.setAttribute('aria-disabled', 'true');
        this.ctaEl.classList.add('is-disabled');
        this.ctaEl.classList.add('is-unavailable');
        this.ctaLabelEl.textContent = this.labels.unavailable;
        return;
      }

      // Enabled "Add to cart" — drop both disabled-flavour classes so the
      // base black-pill styling re-applies cleanly.
      this.ctaEl.removeAttribute('disabled');
      this.ctaEl.setAttribute('aria-disabled', 'false');
      this.ctaEl.classList.remove('is-disabled');
      this.ctaEl.classList.remove('is-unavailable');
      this.ctaLabelEl.textContent = this.labels.add;
    }

    async _addToCart() {
      const variant = this._matchVariant();
      if (!variant || !variant.available || !this._isSelectionComplete()) return;

      const api = window.DDCartApi;
      this.ctaLabelEl.textContent = ADDING_LABEL;
      this.ctaEl.setAttribute('disabled', '');

      // Request the cart-drawer section HTML alongside the add so we can
      // hand it to <cart-drawer>.renderContents() in one shot — matches the
      // pattern used by `dd-sticky-footer.js` and keeps the mini-cart in
      // lockstep with the rest of the theme (single paint, no extra fetch).
      const sections = 'cart-drawer,cart-icon-bubble';

      const qty = Math.max(1, Number(this.quantity) || 1);

      try {
        let result;
        if (api && typeof api.add === 'function') {
          result = await api.add(variant.id, qty, sections);
        } else {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              items: [{ id: variant.id, quantity: qty }],
              sections,
            }),
          });
          if (!res.ok) throw new Error('cart/add failed: ' + res.status);
          result = await res.json();
        }

        this.ctaLabelEl.textContent = ADDED_LABEL;
        this.ctaEl.classList.add('is-added');

        // Render the freshly-returned cart-drawer section into the live
        // <cart-drawer> and open it. renderContents() both swaps the HTML
        // and toggles the drawer open on Dawn's custom element. If it
        // isn't available (older theme builds) fall back to .open().
        try {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) {
            if (result && result.sections && typeof drawer.renderContents === 'function') {
              drawer.renderContents(result);
            } else if (typeof drawer.open === 'function') {
              drawer.open(this.ctaEl);
            }
          }
        } catch (_e) {
          /* non-fatal: drawer open is best-effort */
        }

        try {
          document.dispatchEvent(
            new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'dd-card2', result } })
          );
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } catch (_e) {}

        // Revert the CTA after a short beat so shoppers can add another variant.
        setTimeout(() => {
          this.ctaEl.classList.remove('is-added');
          this.ctaEl.removeAttribute('disabled');
          this.ctaLabelEl.textContent = this.labels.add;
        }, 1600);
      } catch (err) {
        console.warn('[dd-card2] add-to-cart failed', err);
        this.ctaLabelEl.textContent = 'Try again';
        this.ctaEl.classList.remove('is-added');
        this.ctaEl.removeAttribute('disabled');
      }
    }
  }

  window.customElements.define('dd-card2', DdCard2);
})();
